import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ClientSettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({});
  const [clientTypes, setClientTypes] = useState([]);
  const [contactRoles, setContactRoles] = useState([]);
  const [noteTypes, setNoteTypes] = useState([]);
  const [docCategories, setDocCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [saving, setSaving] = useState(false);

  // Inline add forms
  const [newType, setNewType] = useState('');
  const [newRole, setNewRole] = useState('');
  const [newNoteType, setNewNoteType] = useState('');
  const [newDocCat, setNewDocCat] = useState('');

  const fetchAll = () => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      fetch(`${API}/companies/${companyId}/clients-settings`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : {}),
      fetch(`${API}/companies/${companyId}/clients-settings/types`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/companies/${companyId}/clients-settings/contact-roles`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/companies/${companyId}/clients-settings/note-types`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/companies/${companyId}/clients-settings/document-categories`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
    ]).then(([s, t, r, n, d]) => {
      setSettings(s || {});
      setClientTypes(Array.isArray(t) ? t : []);
      setContactRoles(Array.isArray(r) ? r : []);
      setNoteTypes(Array.isArray(n) ? n : []);
      setDocCategories(Array.isArray(d) ? d : []);
    }).catch(() => {})
    .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, [companyId, API]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/clients-settings`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error();
      alert('Settings saved');
    } catch { alert('Failed to save'); }
    finally { setSaving(false); }
  };

  const addItem = async (endpoint, name, refresh) => {
    if (!name.trim()) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/clients-settings/${endpoint}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error();
      refresh();
    } catch { alert('Failed to add'); }
  };

  const deleteItem = async (endpoint, id, refresh) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await fetch(`${API}/companies/${companyId}/clients-settings/${endpoint}/${id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      refresh();
    } catch { alert('Failed to delete'); }
  };

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'types', label: 'Client Types' },
    { key: 'roles', label: 'Contact Roles' },
    { key: 'notes', label: 'Note Types' },
    { key: 'documents', label: 'Document Categories' },
  ];

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;

  return (
    <div style={pageStyle}>
      <h2 style={{ margin: '0 0 20px', fontSize: '1.5rem', fontWeight: 700 }}>⚙️ Client Settings</h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '20px' }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: activeTab === t.key ? 700 : 500,
              color: activeTab === t.key ? '#3b82f6' : '#64748b',
              borderBottom: activeTab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: '-2px',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* General Settings */}
      {activeTab === 'general' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>General Settings</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Client Code Prefix</label>
              <input value={settings.code_prefix || ''} onChange={e => setSettings({ ...settings, code_prefix: e.target.value })} style={formInputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Account Owner Requirement</label>
              <select value={settings.account_owner_requirement || 'optional'} onChange={e => setSettings({ ...settings, account_owner_requirement: e.target.value })} style={formInputStyle}>
                <option value="optional">Optional</option>
                <option value="required_active">Required for Active Clients</option>
                <option value="required_always">Always Required</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Inactivity Threshold (days)</label>
              <input type="number" value={settings.inactivity_threshold_days || 90} onChange={e => setSettings({ ...settings, inactivity_threshold_days: parseInt(e.target.value) || 90 })} style={formInputStyle} />
            </div>
            <div>
              <label style={labelStyle}>New Client Period (days)</label>
              <input type="number" value={settings.new_client_days || 30} onChange={e => setSettings({ ...settings, new_client_days: parseInt(e.target.value) || 30 })} style={formInputStyle} />
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={settings.health_enabled || false} onChange={e => setSettings({ ...settings, health_enabled: e.target.checked })} />
                Health Tracking Enabled
              </label>
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={settings.allow_multiple_companies || false} onChange={e => setSettings({ ...settings, allow_multiple_companies: e.target.checked })} />
                Allow Multiple Company Relationships
              </label>
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={settings.duplicate_detection_enabled || false} onChange={e => setSettings({ ...settings, duplicate_detection_enabled: e.target.checked })} />
                Duplicate Detection Enabled
              </label>
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={settings.on_hold_requires_reason || false} onChange={e => setSettings({ ...settings, on_hold_requires_reason: e.target.checked })} />
                On Hold Requires Reason
              </label>
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={settings.notify_on_assignment || false} onChange={e => setSettings({ ...settings, notify_on_assignment: e.target.checked })} />
                Notify on Assignment
              </label>
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={settings.notify_on_status_change || false} onChange={e => setSettings({ ...settings, notify_on_status_change: e.target.checked })} />
                Notify on Status Change
              </label>
            </div>
          </div>
          <div style={{ marginTop: '20px' }}>
            <button onClick={saveSettings} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Save Settings'}</button>
          </div>
        </div>
      )}

      {/* Client Types */}
      {activeTab === 'types' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>Client Types</h3>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input value={newType} onChange={e => setNewType(e.target.value)} placeholder="New client type..." style={formInputStyle} />
            <button onClick={() => { addItem('types', newType, fetchAll); setNewType(''); }} style={btnPrimary}>Add</button>
          </div>
          {clientTypes.map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{t.name}</span>
                {t.code && <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: '8px' }}>{t.code}</span>}
                {!t.is_active && <span style={{ ...badgeStyle, background: '#fee2e2', color: '#ef4444', marginLeft: '8px' }}>Inactive</span>}
              </div>
              <button onClick={() => deleteItem('types', t.id, fetchAll)} style={actionBtn}>🗑️</button>
            </div>
          ))}
          {clientTypes.length === 0 && <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No client types configured</p>}
        </div>
      )}

      {/* Contact Roles */}
      {activeTab === 'roles' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>Contact Roles</h3>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input value={newRole} onChange={e => setNewRole(e.target.value)} placeholder="New contact role..." style={formInputStyle} />
            <button onClick={() => { addItem('contact-roles', newRole, fetchAll); setNewRole(''); }} style={btnPrimary}>Add</button>
          </div>
          {contactRoles.map(r => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.name}</span>
                {r.is_system && <span style={{ ...badgeStyle, background: '#dbeafe', color: '#3b82f6', marginLeft: '8px' }}>System</span>}
              </div>
              {!r.is_system && <button onClick={() => deleteItem('contact-roles', r.id, fetchAll)} style={actionBtn}>🗑️</button>}
            </div>
          ))}
          {contactRoles.length === 0 && <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No contact roles configured</p>}
        </div>
      )}

      {/* Note Types */}
      {activeTab === 'notes' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>Note Types</h3>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input value={newNoteType} onChange={e => setNewNoteType(e.target.value)} placeholder="New note type..." style={formInputStyle} />
            <button onClick={() => { addItem('note-types', newNoteType, fetchAll); setNewNoteType(''); }} style={btnPrimary}>Add</button>
          </div>
          {noteTypes.map(n => (
            <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{n.name}</span>
              <button onClick={() => deleteItem('note-types', n.id, fetchAll)} style={actionBtn}>🗑️</button>
            </div>
          ))}
          {noteTypes.length === 0 && <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No note types configured</p>}
        </div>
      )}

      {/* Document Categories */}
      {activeTab === 'documents' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>Document Categories</h3>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input value={newDocCat} onChange={e => setNewDocCat(e.target.value)} placeholder="New category..." style={formInputStyle} />
            <button onClick={() => { addItem('document-categories', newDocCat, fetchAll); setNewDocCat(''); }} style={btnPrimary}>Add</button>
          </div>
          {docCategories.map(d => (
            <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{d.name}</span>
                {d.track_expiry && <span style={{ ...badgeStyle, background: '#fef3c7', color: '#d97706', marginLeft: '8px' }}>Tracks Expiry</span>}
              </div>
              <button onClick={() => deleteItem('document-categories', d.id, fetchAll)} style={actionBtn}>🗑️</button>
            </div>
          ))}
          {docCategories.length === 0 && <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No document categories configured</p>}
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1000px' };
const cardStyle = { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' };
const formInputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
const actionBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '2px 4px' };
